import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { ICON_CLOSE, ICON_MOVE_OR_SCROLL_DOWN, ICON_MOVE_OR_SCROLL_UP } from 'Config/Icons';

import styles from './CollapsibleModal.module.css';
import { Modal } from '../Modal';
import { ModalHeaderProps } from '../ModalHeader/ModalHeader';

interface CollapsibleModalBaseProps {
    headerText: ModalHeaderProps;
    hideModal: () => void;
}

interface CollapsibleModalLoadingProps extends CollapsibleModalBaseProps {
    isLoading: true;

    displayItems?: never;
    /** If defined, this is displayed in place of the loading animation. Pass in a value if loading data for the content of the modal fails. */
    failureMessage?: string;
    previewAmount?: never;
}

interface CollapsibleModalLoadedProps extends CollapsibleModalBaseProps {
    isLoading?: false;

    displayItems: JSX.Element[];
    previewAmount?: number;
}

export type CollapsibleModalProps = CollapsibleModalLoadingProps | CollapsibleModalLoadedProps;

/**
 * Renders a modal with a list of elements that may be quite long. The user initially sees a limited number of items, and can choose to show/hide the full list.
 * If data needs to be loaded before the content of the modal can be displayed, pass in `isLoading`, `hideModal`, and `failureMessage` (if there was a failure loading data). `Modal` should not be used directly for the loading state, as switching between two different components with the darkened background causes an unpleasant flicker on the screen.
 *
 * IMPORTANT: This follows the same patterns as `AssociatedControlsModal`. If a change is made here, consider whether the same change needs to be made there, and vice-versa.
 */
export const CollapsibleModal = (props: CollapsibleModalProps): JSX.Element => {
    const defaultAmountShown = props.previewAmount ?? 10;
    const [showAll, setShowAll] = useState<boolean>(false);

    if (props.isLoading) {
        return <Modal open isLoading closeModal={props.hideModal} header={props.headerText.text} subHeader={props.headerText.secondaryText} failureMessage={props.failureMessage} />;
    }

    return (
        <Modal open header={props.headerText.text} subHeader={props.headerText.secondaryText}>
            {props.displayItems.slice(0, showAll ? undefined : defaultAmountShown)}
            <div className={styles.showMoreButton}>
                {props.displayItems.length > defaultAmountShown && (
                    <Button variant="linkText" size="lg" onClick={() => setShowAll(!showAll)}>
                        {showAll ? (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_UP} />
                                {` Show Less`}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_DOWN} />
                                {` Show ${props.displayItems.length - defaultAmountShown} More`}
                            </>
                        )}
                    </Button>
                )}
            </div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};
